import Carousel from '@carvana/ds-carousel';
import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { useContext } from 'react';
import { useInView } from 'react-intersection-observer';
import { INTERACTION_TO_ACTION } from '~/constants/common/CarouselAnalytics';
import { useIsMobile, useScreenSize } from '~/context/common/DeviceContext';
import useIsInShaqHomeTakeoverExperiment from '~/hooks/common/experiments/useIsInShaqHomeTakeoverExperiment';
import AffordableCarsCard from './Cards/AffordableCarsCard';
import FreeShipping from './Cards/FreeShippingCard';
import FuelEfficientCard from './Cards/FuelEfficientCard';
import GreatDealsCard from './Cards/GreatDealsCard';
import NeedItFast from './Cards/NeedItFast';
import SectionWrapper from '../SectionWrapper';

type OwnProps = {
  eventName: string;
  hasBackground?: boolean;
};

export default function BudgetFriendlyCarousel({
  eventName,
  hasBackground = true
}: OwnProps) {
  const { trackEvent } = useContext(AnalyticsContext);
  const isMobile = useIsMobile();
  const isInShaqExperiment = useIsInShaqHomeTakeoverExperiment();
  const { width } = useScreenSize();
  const { ref, inView } = useInView();
  const isAtLeast1350 = width >= 1350;

  const greatDealsCard = <GreatDealsCard key="great-deals" />;
  const needItFastCard = <NeedItFast key="need-it-fast" />;

  const cards = [
    <AffordableCarsCard key="affordable-cars" />,
    ...(isInShaqExperiment
      ? [needItFastCard, greatDealsCard]
      : [greatDealsCard, needItFastCard]),
    <FuelEfficientCard key="fuel-efficient" />,
    <FreeShipping key="free-shipping" />
  ];

  return (
    <SectionWrapper
      ref={ref}
      analyticsSection="Budget Friendly"
      eventName={eventName}
      hasBackground={hasBackground}
      hasBottomSpacing
      className="lg:px-4xl"
      style={
        {
          '--animation-play-state': inView ? 'running' : 'paused'
        } as React.CSSProperties
      }
    >
      <Carousel
        ariaLabel="Budget Friendly"
        data-testid="budget-friendly-carousel"
        classes={{
          slide:
            'pl-xl first:pl-2xl last:pr-2xl sm:pl-2xl sm:first:pl-4xl sm:last:pr-4xl md:first:pl-3xl md:last:pr-3xl lg:pl-0 lg:first:pl-0 lg:last:pr-0 pb-[2px]',
          slides: 'gap-0 lg:gap-2xl',
          topSection:
            'px-2xl sm:px-4xl md:px-3xl gap-0 lg:px-0 sm:grid-cols-[1fr_auto]'
        }}
        headerContent={
          <h2 className="text-text-strong t-heading-lg sm:mb-sm md:t-heading-xl">
            Explore cars you’ll love
          </h2>
        }
        hideScrollBar
        hidePagination={!isMobile}
        slideByVisible={!isAtLeast1350}
        trackEvent={event => {
          trackEvent('Budget Friendly', {
            ...event,
            action: INTERACTION_TO_ACTION[event.interactionType]
          });
        }}
      >
        {cards}
      </Carousel>
    </SectionWrapper>
  );
}
